import { Component, Input, ChangeDetectionStrategy, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkOverlayOrigin, OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';
import { Router } from '@angular/router';
import { ChangeDetectorRef } from '@angular/core';
import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';
import { ModalService } from '../../../../shared/services/modal.service';
import { FiDataService } from '../../../../core/services/fi-data.service';
import { AccountAggregatorService } from '../../../../core/services/account-aggregator.service';
import { Subscription } from 'rxjs';

export interface Transaction {
  icon: string;
  label: string;
  date: string;
  time: string;
  amount: string;
  type: 'withdraw' | 'deposit';
}

export interface Account {
  number: string;
  balance: string;
  currency: string;
  transactions: Transaction[];
}

@Component({
  selector: 'app-account-summary',
  standalone: true,
  imports: [CommonModule, CdkOverlayOrigin, WidgetEdit, OverlayModule],
  templateUrl: './account-summary.html',
  styleUrl: './account-summary.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSummary implements OnInit, OnDestroy {
  @Input() title = 'ACCOUNTS SUMMARY';
  @Input() size: 'small' | 'medium' | 'half' | 'full' = 'medium';
  @Input() index = 0;
  @Input() isEditMode = false;
  @Input() isAddMode = false;
  @Output() addWidget = new EventEmitter<void>();
  @Input() activeDropdownIndex: number | null = null;
  @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
  @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
  @Input() changeWidgetSize!: (index: number, size: any) => void;
  @Input() removeWidget!: (index: number) => void;

  currentIndex = 0;
  accounts: Account[] = [];
  private accountsData: any[] = []; // Store raw API data for modal
  private subs = new Subscription();

  /*start of logic transaction modal*/
  constructor(
    private modalService: ModalService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private fiDataService: FiDataService,
    private accountAggregatorService: AccountAggregatorService
  ) { }

  ngOnInit(): void {
    this.loadAccountData();
  }

  /**
   * Load account data from API
   * Replace this consentID with actual value from user session/config
   */
  private loadAccountData(): void {
    // TODO: Replace with actual consentID from user session
    let consentID: any = localStorage.getItem('active_consent_id');; // This should come from user authentication/session

    this.subs.add(this.fiDataService.getAllFIData(consentID, 'cache').subscribe({
      next: (response) => {
        this.accountsData = this.accountAggregatorService.parseAccountAggregatorResponse(response);
        this.accounts = this.accountAggregatorService.transformAccountsForSummaryWidget(this.accountsData);

        this.cdr.markForCheck(); // Trigger change detection since we're using OnPush
      },
      error: (error) => {
        console.error('Error loading account data:', error);
      }
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // Recent Transactions Logic
  isRecentTransactionsOpen = false;
  transactionCurrentPage = 0;
  transactionPageSize = 5;
  weeklyLimit = 2850.75;
  maxWeeklyLimit = 50000;

  overlayPositions: ConnectedPosition[] = [
    {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: 0
    }
  ];

  get paginatedRecentTransactions() {
    return this.currentAccount.transactions;
  }

  toggleRecentTransactions(event: Event) {
    if (this.isEditMode || this.isAddMode) return;
    event.stopPropagation();
    this.isRecentTransactionsOpen = !this.isRecentTransactionsOpen;
    this.cdr.detectChanges();
  }

  closeRecentTransactions(event?: Event) {
    if (event) event.stopPropagation();
    this.isRecentTransactionsOpen = false;
    this.cdr.detectChanges();
  }

  nextTransactionPage() {
    if ((this.transactionCurrentPage + 1) * this.transactionPageSize < this.currentAccount.transactions.length) {
      this.transactionCurrentPage++;
      this.cdr.detectChanges();
    }
  }

  previousTransactionPage() {
    if (this.transactionCurrentPage > 0) {
      this.transactionCurrentPage--;
      this.cdr.detectChanges();
    }
  }

  navigateToTransactions(event: Event) {
    if (this.isAddMode) return;
    this.closeRecentTransactions(event);

    let accountParam: string | null = null;
    if (this.currentAccount && this.currentAccount.number) {
      const match = this.currentAccount.number.match(/\d{4}$/);
      if (match) {
        accountParam = match[0];
      }
    }

    if (accountParam) {
      this.router.navigate(['/transactions'], { queryParams: { account: accountParam } });
    } else {
      this.router.navigate(['/transactions']);
    }
  }
  /*end of logic */

  get currentAccount(): Account {
    return this.accounts[this.currentIndex] || {
      number: '',
      balance: '0',
      currency: 'INR',
      transactions: []
    };
  }

  get visibleTransactions(): Transaction[] {
    return this.currentAccount.transactions.slice(0, 1);
  }

  get isDarkMode(): boolean {
    return document.body.classList.contains('dark-mode');
  }

  next() {
    if (this.isAddMode) return;
    if (this.currentIndex < this.accounts.length - 1) {
      this.currentIndex++;
    }
  }

  prev() {
    if (this.isAddMode) return;
    if (this.currentIndex > 0) {
      this.currentIndex--;
    }
  }

  get canGoNext(): boolean {
    return this.currentIndex < this.accounts.length - 1;
  }

  get canGoPrev(): boolean {
    return this.currentIndex > 0;
  }

  get totalBalance(): string {
    const total = this.accounts.reduce((sum, acc) => {
      const val = parseFloat(acc.balance.replace(/,/g, ''));
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
    return total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  openModal() {
    if (!this.isEditMode && !this.isAddMode) {
      // Use cached accountsData if available
      if (this.accountsData && this.accountsData.length > 0) {
        const modalData = this.accountAggregatorService.transformAccountsForModal(this.accountsData);
        this.modalService.openAccountSummaryModal(modalData.accounts, modalData.creditCards);
      } else {
        // No data loaded yet, open with empty modal
        this.modalService.openAccountSummaryModal();
      }
    }
  }
}
